// src/components/results/RetryPage.jsx
//
// "Retry my mistakes": the questions a student missed, asked again after a pause, one
// at a time, with just the context needed (the text, the gap sentence, the ads, or the
// Hören clip). Right → done; wrong → back in 7 days (src/services/retryService.js).
// Premium only: free users see the set locked, with the upsell.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Lock, Pause, Play } from 'lucide-react';
import { useCaveat } from '../mockExam/RedPenCards';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import examService from '../../services/examService';
import storageService from '../../services/storageService';
import { dueItems, getRetrySet, recordRetryAnswer } from '../../services/retryService';
import { isAnswerCorrect, isVerdictQuestion } from '../../utils/answerCheck';
import ExplanationPanel from '../practice/ExplanationPanel';
import AhaUpsellModal from '../ui/AhaUpsellModal';
import { gapSentence, itemKey } from './storyText';

const HAND = { fontFamily: "'Caveat', cursive" };
const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const SECTION_NAME = { lesen: 'Lesen', sprachbausteine: 'Sprachbausteine', hoeren: 'Hören' };
const toMs = (v) => (v?.toMillis ? v.toMillis() : Number(v) || 0);

/** What to show for one question: a prompt, optional context, and the choices. */
function taskOf(q, part, t, provider) {
  const c = part.content || {};
  // A shared list of people, texts or sentences: id plus a short name, or the sentence itself
  const pool = (items) => items.filter((o) => !o.isExample).map((o) => ({ value: o.id, label: `${o.id}  ${o.title || o.author || o.name || o.text || ''}`.trim(), long: true }));
  if (typeof q.correctAnswer === 'boolean') {
    return { prompt: q.statement, options: [{ value: true, label: t('examResults.answerTrue') }, { value: false, label: t('examResults.answerFalse') }] };
  }
  // Verdicts stored as words: richtig/falsch, ja/nein, telc C1 +/-/x (steht nicht im Text)
  if (!q.options?.length && isVerdictQuestion(q, part)) {
    const key = String(q.correctAnswer).toLowerCase();
    const person = q.personId && (c.texts || []).find((x) => String(x.id) === String(q.personId));
    const options = ['+', '-', 'x'].includes(key)
      ? [{ value: '+', label: t('examResults.answerTrue') }, { value: '-', label: t('examResults.answerFalse') }, { value: 'x', label: t('examResults.answerNotStated') }]
      : ['ja', 'nein'].includes(key)
        ? [{ value: 'Ja', label: t('examResults.yes') }, { value: 'Nein', label: t('examResults.no') }]
        : [{ value: 'Richtig', label: t('examResults.answerTrue') }, { value: 'Falsch', label: t('examResults.answerFalse') }];
    // Goethe B1 Lesen 4 asks the same ja/nein question for every person: it sits in the instructions
    return { prompt: q.statement || q.questionText || part.instructions, context: person?.content || person?.text, longText: person ? undefined : c.mainText, options };
  }
  if (q.speakers?.length) return { prompt: q.questionText, options: q.speakers.map((sp) => ({ value: sp, label: sp })) };
  // telc C1 Lesen 2: which paragraph (a) … e) inside the text)
  if (part.taskType === 'matching-questions-to-paragraphs') {
    const ids = [...new Set([...(c.mainText || '').matchAll(/^[*\s(]*([a-z])\)/gm)].map((m) => m[1]))];
    return { prompt: q.questionText, longText: c.mainText, options: ids.map((id) => ({ value: id, label: t('examResults.story.paragraph', { id }) })) };
  }
  // Goethe B2 Lesen 4: a headline matched to the text whose writer fits it
  if (c.headlines && c.texts) {
    const h = c.headlines.find((x) => String(x.id) === String(q.questionNumber));
    return { prompt: h?.text, options: pool(c.texts) };
  }
  if (c.headings) {
    const text = (c.texts || []).find((x) => String(x.id) === String(q.questionNumber));
    return { prompt: t('examResults.story.whichHeading', { n: q.questionNumber }), context: text?.text, options: c.headings.map((h) => ({ value: h.id, label: `${h.id}  ${h.text}` })) };
  }
  if (c.situations) {
    const sit = c.situations.find((x) => String(x.id) === String(q.questionNumber));
    return { prompt: sit?.text, options: [...(c.texts || c.advertisements || []).map((a) => ({ value: a.id, label: `${a.id}  ${a.text}`, long: true })), { value: /telc/i.test(provider || '') ? 'x' : '0', label: t('examResults.answerNoMatch') }] };
  }
  if (c.wordBank) return { prompt: gapSentence(c.text, q.blankNumber), options: c.wordBank.map((w) => ({ value: w, label: w })) };
  // A list shared by the whole part: sentence gaps, people, books, statements per speaker
  if (!q.options?.length && (c.options || c.texts)) {
    const gap = q.blankNumber != null ? gapSentence(c.mainText || c.text, q.blankNumber) : '';
    // "nobody says this" (0) where the part uses it, e.g. Goethe C1 Lesen 4
    const none = (part.questions || []).some((x) => String(x.correctAnswer) === '0') ? [{ value: '0', label: t('examResults.answerNoMatch') }] : [];
    return {
      prompt: q.questionText || q.statement || gap || t('examResults.story.whichStatement', { n: q.questionNumber }),
      longText: gap ? undefined : c.mainText,
      options: [...pool(c.options || c.texts), ...none],
    };
  }
  return {
    prompt: q.questionText || q.statement || gapSentence(c.mainText || c.text, q.blankNumber),
    longText: c.mainText,
    options: (q.options || []).map((o) => ({ value: o.id, label: o.text })),
  };
}

/** Plays only the part of the section recording that belongs to one question. */
function ClipPlayer({ url, start, end }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (playing) { a.pause(); return; }
    if (a.currentTime < start || a.currentTime >= end - 0.2) a.currentTime = start;
    a.play();
  };
  return (
    <div className="flex items-center gap-3 bg-stone-100 rounded-xl px-3 py-2.5">
      <audio ref={ref} src={url} preload="metadata"
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => { const a = e.currentTarget; setPos(a.currentTime); if (a.currentTime >= end) a.pause(); }} />
      <button type="button" onClick={toggle} aria-label={playing ? t('examResults.story.retry.pause') : t('examResults.story.retry.play')}
        className="rs-focus w-12 h-12 shrink-0 rounded-full bg-stone-900 text-white inline-flex items-center justify-center touch-manipulation active:scale-95 transition-transform">
        {playing ? <Pause className="w-5 h-5" aria-hidden="true" /> : <Play className="w-5 h-5 ml-0.5" aria-hidden="true" />}
      </button>
      <div className="flex-1 h-1.5 bg-stone-300 rounded-full overflow-hidden">
        <div className="h-full bg-[#006D77]" style={{ width: `${Math.max(0, Math.min(100, ((pos - start) / (end - start)) * 100))}%` }} />
      </div>
      <span className="text-sm font-semibold text-stone-600 tabular-nums">{Math.round(end - start)} s</span>
    </div>
  );
}

export default function RetryPage({ uid, setId, isFree, onBack }) {
  const { t, i18n } = useTranslation();
  useCaveat();
  const navigate = useLocalizedNavigate();
  const [set, setSet] = useState(null);
  const [exam, setExam] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const [queue, setQueue] = useState(null);       // question ids for this sitting
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState(null);     // { value, correct }
  const [score, setScore] = useState({ right: 0, wrong: 0 });
  const [upsell, setUpsell] = useState(false);
  const sittingStart = useRef(Date.now());   // tries before this moment were earlier sittings

  // the set, then its paper (and the Hören recording when the set has Hören items)
  useEffect(() => {
    if (!uid || !setId) return undefined;
    let cancelled = false;
    (async () => {
      const s = await getRetrySet(uid, setId);
      if (!s) throw new Error('not-found');
      const variants = examService.getAvailableExams()[s.provider]?.[s.level.toUpperCase()] || [];
      const config = variants.find((v) => v.file === s.examFileName);
      const paper = await examService.loadExam(s.provider.toUpperCase(), s.level.toUpperCase(), config?.id);
      if (cancelled) return;
      setSet(s); setExam(paper);
      const ids = new Set();
      (paper.sections || []).forEach((sec) => (sec.parts || []).forEach((pt) => (pt.questions || []).forEach((q) => ids.add(itemKey({ partId: pt.partId, questionId: q.questionId })))));
      setQueue(dueItems(s).filter((it) => ids.has(itemKey(it))).map(itemKey));
      if ((s.items || []).some((it) => it.section === 'hoeren')) {
        const path = storageService.generateAudioPath(s.provider, s.level, 'listening', s.examFileName, paper.variantName);
        const url = path ? await storageService.getAudioUrl(path) : null;
        if (!cancelled) setAudioUrl(url);
      }
    })().catch((err) => { if (!cancelled) setError(err.message || 'error'); });
    return () => { cancelled = true; };
  }, [uid, setId]);

  const qIndex = useMemo(() => {
    const m = {};
    (exam?.sections || []).forEach((s) => (s.parts || []).forEach((p) => (p.questions || []).forEach((q) => { m[itemKey({ partId: p.partId, questionId: q.questionId })] = { q, part: p }; })));
    return m;
  }, [exam]);

  const dueMs = toMs(set?.dueAt);
  const date = (ms) => new Date(ms).toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long' });
  const shell = (children) => (
    <div className="min-h-screen bg-[#FAF8F3] text-stone-900">
      <div className="max-w-[760px] mx-auto px-4 sm:px-6 pt-6 pb-32 flex flex-col gap-6">
        <button type="button" onClick={onBack} className="rs-focus self-start min-h-[44px] inline-flex items-center gap-1.5 text-sm font-semibold">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />{t('examResults.backToDashboard')}
        </button>
        {children}
      </div>
      <AhaUpsellModal open={upsell} onDismiss={() => setUpsell(false)} onUpgrade={() => setUpsell(false)} context="exam" />
    </div>
  );

  if (error) return shell(<p className="text-stone-700">{t('examResults.story.retry.notFound')}</p>);
  if (!set || !exam || !queue) return shell(<p className="text-stone-500">{t('examResults.story.retry.loading')}</p>);

  const open = (set.items || []).filter((it) => !it.done).length;
  const title = (
    <header className="flex flex-col gap-2">
      <p className="m-0 text-[13px] font-semibold tracking-[.08em] uppercase text-stone-600">{[set.provider, set.level].join(' ').toUpperCase()} · {set.examName}</p>
      <h1 className="m-0 text-[30px] sm:text-[40px] leading-[1.05] font-bold tracking-tight" style={DISPLAY}>{t('examResults.story.retry.title')}</h1>
    </header>
  );

  if (isFree) {
    return shell(<>
      {title}
      <section className="rs-paper px-6 pt-6 pb-7 flex flex-col gap-3" style={{ borderTop: '6px solid #DC2626', backgroundPosition: '0 20px' }}>
        <span className="text-[28px] leading-none text-red-600" style={HAND}>{t('examResults.story.retry.note', { count: open, date: date(dueMs) })}</span>
        <p className="m-0 text-stone-700 leading-relaxed">{t('examResults.story.retry.why')}</p>
        <button type="button" onClick={() => setUpsell(true)} className="rs-focus self-start min-h-[48px] px-5 rounded-[10px] bg-stone-900 text-white font-bold inline-flex items-center gap-2 touch-manipulation">
          <Lock className="w-4 h-4" aria-hidden="true" />{t('examResults.story.retry.unlock')}
        </button>
      </section>
    </>);
  }

  if (!queue.length) {
    return shell(<>
      {title}
      <section className="rs-paper px-6 pt-6 pb-7 flex flex-col gap-3" style={{ borderTop: '6px solid #006D77', backgroundPosition: '0 20px' }}>
        <span className="text-[28px] leading-tight text-[#006D77]" style={HAND}>
          {open ? t('examResults.story.retry.notYet', { count: open, date: date(dueMs) }) : t('examResults.story.retry.allDone')}
        </span>
        <p className="m-0 text-stone-700 leading-relaxed">{t('examResults.story.retry.why')}</p>
      </section>
    </>);
  }

  // finished this sitting
  if (index >= queue.length) {
    const back = (set.items || []).filter((it) => !it.done);
    return shell(<>
      {title}
      <section className="rs-paper rs-up px-6 pt-6 pb-7 flex flex-col gap-3" style={{ borderTop: '6px solid #1C1917', backgroundPosition: '0 20px' }}>
        <span className="text-[56px] leading-none" style={HAND}>{score.right} / {queue.length}</span>
        <p className="m-0 text-[17px] font-semibold">{t('examResults.story.retry.doneRight', { count: score.right })}</p>
        <p className="m-0 text-stone-700">
          {back.length ? t('examResults.story.retry.comeBack', { count: back.length, date: date(Math.min(...back.map((it) => it.dueAt))) }) : t('examResults.story.retry.allDone')}
        </p>
        <button type="button" onClick={() => navigate('/dashboard')} className="rs-focus self-start mt-2 min-h-[48px] px-5 rounded-[10px] bg-stone-900 text-white font-bold touch-manipulation">
          {t('examResults.backToDashboard')}
        </button>
      </section>
    </>);
  }

  const item = (set.items || []).find((it) => itemKey(it) === queue[index]);
  const { q, part } = qIndex[itemKey(item)] || {};
  const task = taskOf(q, part, t, set.provider);
  const clip = item.section === 'hoeren' && q.audioClip && audioUrl ? q.audioClip : null;

  const choose = async (value) => {
    if (picked) return;
    const correct = isAnswerCorrect(value, q, part);
    setPicked({ value, correct });
    setScore((s) => (correct ? { ...s, right: s.right + 1 } : { ...s, wrong: s.wrong + 1 }));
    try { setSet(await recordRetryAnswer(uid, set, itemKey(item), correct)); }
    catch (err) { console.warn('Retry answer not saved:', err); }
  };
  const next = () => { setPicked(null); setIndex((i) => i + 1); };

  return shell(<>
    {title}
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
        <div className="h-full bg-stone-900 transition-[width] duration-500" style={{ width: `${(index / queue.length) * 100}%` }} />
      </div>
      <span className="text-sm font-semibold text-stone-600 tabular-nums">{index + 1} / {queue.length}</span>
    </div>

    <article key={itemKey(item)} className="rs-panel rs-paper px-5 sm:px-6 pt-5 pb-6 flex flex-col gap-4" style={{ borderTop: `6px solid ${picked ? (picked.correct ? '#006D77' : '#DC2626') : '#1C1917'}`, backgroundPosition: '0 46px' }}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-bold text-stone-600">{SECTION_NAME[item.section]} · {t('examResults.story.teil', { n: part.partNumber })} · {t('examResults.story.questionN', { n: item.number })}</span>
        {item.confident && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-stone-900 text-white">{t('examResults.story.youWereSure')}</span>}
        {(item.tries || []).some((x) => x.at < sittingStart.current) && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-800">{t('examResults.story.retry.again')}</span>}
      </div>
      {clip && <ClipPlayer url={audioUrl} start={clip.start} end={clip.end} />}
      {item.section === 'hoeren' && !clip && <p className="m-0 text-sm text-stone-500">{t('examResults.story.retry.noClip')}</p>}
      {task.context && <p className="m-0 text-[15px] leading-relaxed text-stone-800 bg-stone-50 border border-stone-200 rounded-lg p-4 whitespace-pre-line">{task.context}</p>}
      {task.longText && (
        <details className="bg-stone-50 border border-stone-200 rounded-lg">
          <summary className="cursor-pointer min-h-[44px] px-4 py-2.5 font-semibold text-sm">{t('examResults.story.retry.readText')}</summary>
          <p className="m-0 px-4 pb-4 text-[15px] leading-relaxed whitespace-pre-line">{task.longText.replace(/\*\*/g, '')}</p>
        </details>
      )}
      {task.prompt && <p className="m-0 text-lg sm:text-xl font-semibold leading-snug"><span className="rs-write">{task.prompt}</span></p>}

      <div className={`flex gap-2.5 ${task.options.some((o) => o.long) ? 'flex-col' : 'flex-wrap'}`}>
        {task.options.map((o, i) => {
          const key = picked && isAnswerCorrect(o.value, q, part);
          const mine = picked && String(picked.value) === String(o.value);
          const look = !picked ? 'bg-white border-stone-300 text-stone-900 hover:border-stone-900'
            : key ? 'bg-[#E6F4F1] text-[#006D77] border-[#006D77]'
              : mine ? 'bg-white text-red-800 border-red-600' : 'bg-stone-100 text-stone-400 border-transparent';
          return (
            <button key={String(o.value)} type="button" disabled={!!picked} onClick={() => choose(o.value)}
              className={`rs-opt rs-focus relative text-left inline-flex items-center gap-2 min-h-[48px] px-4 py-2 border-2 font-semibold text-[15px] leading-snug touch-manipulation transition-colors ${o.long ? 'rounded-xl' : 'rounded-[24px]'} ${look}`}
              style={{ animationDelay: `${0.3 + i * 0.05}s` }}>
              <span className={o.long ? 'line-clamp-3' : ''}>{o.label}</span>
              {mine && !key && (
                <svg viewBox="0 0 100 20" preserveAspectRatio="none" className="absolute left-2.5 right-2.5 top-0 w-[calc(100%-20px)] h-full overflow-visible pointer-events-none" aria-hidden="true">
                  <path className="rs-stroke rs-draw" pathLength="1" stroke="#DC2626" strokeWidth="3" d="M2 13 C 30 7, 70 9, 98 6" style={{ animationDuration: '.45s' }} />
                </svg>
              )}
              {key && (
                <svg viewBox="0 0 24 20" className="shrink-0 w-5 h-[17px] overflow-visible" aria-hidden="true">
                  <path className="rs-stroke rs-draw" pathLength="1" stroke="#006D77" strokeWidth="3" d="M2 11 l6 6 l14 -15" style={{ animationDelay: '.3s', animationDuration: '.4s' }} />
                </svg>
              )}
            </button>
          );
        })}
      </div>

      {picked && (
        <div className="rs-later flex flex-col gap-3" style={{ animationDelay: '.5s' }}>
          <p className="m-0 text-[26px] leading-tight" style={{ ...HAND, color: picked.correct ? '#006D77' : '#DC2626' }}>
            {picked.correct ? t('examResults.story.retry.right') : t('examResults.story.retry.wrong')}
          </p>
          {q.smartTags?.evidence && (
            <p className="m-0 text-[15px] text-stone-700">{t('examResults.story.textSays')} <span className="italic underline decoration-red-600 decoration-2 underline-offset-4">„{q.smartTags.evidence}“</span></p>
          )}
          {q.explanation && <ExplanationPanel explanation={q.explanation} />}
          <button type="button" onClick={next} className="rs-focus self-end min-h-[48px] px-6 rounded-[10px] bg-stone-900 text-white font-bold touch-manipulation active:scale-[0.98] transition-transform">
            {index + 1 < queue.length ? t('examResults.story.retry.next') : t('examResults.story.retry.finish')} →
          </button>
        </div>
      )}
    </article>
  </>);
}
